// ─── PerfAuditOverlay ─────────────────────────────────────────
// Phase 1.6 dev-only QA overlay. Strictly gated on `?perfaudit=1`.
// Never shown to normal guests. Read-only. No network calls.
//
// What it tracks (sampled once per second so the overlay itself is cheap):
//   • Scroll FPS (rAF-based, rolling window)
//   • Active `will-change` elements within the public-invite tree
//   • Fixed-position elements within the public-invite tree
//   • Large <img> count (naturalWidth > 1800px) within the public-invite tree
//   • Renderer info passed in via props (layoutVersion, layoutMode, engine)
//
// Warnings auto-surface when thresholds trip:
//   • Fixed background active
//   • High will-change count (>10)
//   • Heavy image (>1800px natural width)
//   • Nested scroll container (overflow auto/scroll trap inside tree)

import React, { useEffect, useRef, useState } from 'react';

const ROOT_SELECTOR = '.public-invite-page';

function countWillChange() {
  const root = document.querySelector(ROOT_SELECTOR);
  if (!root) return 0;
  let n = 0;
  root.querySelectorAll('*').forEach((el) => {
    const wc = getComputedStyle(el).willChange;
    if (wc && wc !== 'auto') n++;
  });
  return n;
}

function countFixed() {
  const root = document.querySelector(ROOT_SELECTOR);
  if (!root) return { count: 0, hasBg: false };
  let n = 0;
  let hasBg = false;
  root.querySelectorAll('*').forEach((el) => {
    if (getComputedStyle(el).position === 'fixed') {
      n++;
      if (el.matches('[data-testid="global-bg-texture"]')) hasBg = true;
    }
  });
  return { count: n, hasBg };
}

function countLargeImages() {
  const root = document.querySelector(ROOT_SELECTOR);
  if (!root) return 0;
  let n = 0;
  root.querySelectorAll('img').forEach((img) => {
    if (img.naturalWidth && img.naturalWidth > 1800) n++;
  });
  return n;
}

function detectScrollTrap() {
  const root = document.querySelector(ROOT_SELECTOR);
  if (!root) return false;
  let trap = false;
  root.querySelectorAll('*').forEach((el) => {
    if (trap) return;
    const cs = getComputedStyle(el);
    if ((cs.overflowY === 'scroll' || cs.overflowY === 'auto') &&
        el.scrollHeight > el.clientHeight + 8) {
      trap = true;
    }
  });
  return trap;
}

export default function PerfAuditOverlay({ enabled, layoutVersion, engine, layoutMode }) {
  const [stats, setStats] = useState({
    fps: 0,
    willChange: 0,
    fixed: 0,
    fixedBg: false,
    largeImages: 0,
    scrollTrap: false,
  });
  const [collapsed, setCollapsed] = useState(false);
  const fpsFramesRef = useRef([]);
  const rafRef = useRef(null);

  // FPS sampler — keeps a 1-second sliding window of frame timestamps.
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = (now) => {
      const arr = fpsFramesRef.current;
      arr.push(now);
      // Drop frames older than 1 second
      while (arr.length && now - arr[0] > 1000) arr.shift();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [enabled]);

  // Periodic DOM scan — throttled to 1s to keep cost negligible.
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const scan = () => {
      if (cancelled) return;
      const fps = fpsFramesRef.current.length;
      const fx = countFixed();
      setStats({
        fps,
        willChange: countWillChange(),
        fixed: fx.count,
        fixedBg: fx.hasBg,
        largeImages: countLargeImages(),
        scrollTrap: detectScrollTrap(),
      });
    };
    scan();
    const id = setInterval(scan, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled]);

  if (!enabled) return null;

  const warnings = [];
  if (stats.fixedBg) warnings.push('Fixed background active');
  if (stats.willChange > 10) warnings.push(`High will-change (${stats.willChange})`);
  if (stats.largeImages > 0) warnings.push(`Heavy image ×${stats.largeImages}`);
  if (stats.scrollTrap) warnings.push('Nested scroll container');

  const fpsColor = stats.fps >= 50 ? '#34d399'
                 : stats.fps >= 30 ? '#fbbf24'
                 : '#f87171';

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed bottom-3 right-3 z-[9999] text-[10px] font-mono px-2 py-1 rounded-md bg-black/70 text-white shadow-lg"
        data-testid="perf-audit-toggle"
        style={{ pointerEvents: 'auto' }}
      >
        perf · <span style={{ color: fpsColor }}>{stats.fps}fps</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-3 right-3 z-[9999] text-[10px] font-mono leading-tight"
      style={{
        pointerEvents: 'auto',
        background: 'rgba(0,0,0,0.72)',
        color: '#f5f5f5',
        padding: '8px 10px',
        borderRadius: '8px',
        minWidth: 180,
        maxWidth: 240,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        backdropFilter: 'none',
      }}
      data-testid="perf-audit-overlay"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="uppercase tracking-wide opacity-60" style={{ fontSize: 9 }}>
          Perf audit
        </span>
        <button
          onClick={() => setCollapsed(true)}
          className="opacity-50 hover:opacity-100"
          aria-label="Collapse"
          data-testid="perf-audit-collapse"
        >
          ×
        </button>
      </div>

      <div className="space-y-0.5">
        <div data-testid="perf-fps">
          fps: <span style={{ color: fpsColor, fontWeight: 600 }}>{stats.fps}</span>
        </div>
        <div data-testid="perf-will-change">
          will-change: <span style={{ color: stats.willChange > 10 ? '#fbbf24' : '#f5f5f5' }}>{stats.willChange}</span>
        </div>
        <div data-testid="perf-fixed">
          fixed: <span style={{ color: stats.fixedBg ? '#f87171' : '#f5f5f5' }}>{stats.fixed}</span>
        </div>
        <div data-testid="perf-large-images">
          big imgs: <span style={{ color: stats.largeImages > 0 ? '#fbbf24' : '#f5f5f5' }}>{stats.largeImages}</span>
        </div>
      </div>

      <div className="mt-1.5 pt-1.5 border-t border-white/15 opacity-80" style={{ fontSize: 9 }}>
        <div data-testid="perf-layout-version">
          layout: <span style={{ color: layoutVersion === 'section-v1' ? '#34d399' : '#a8a29e' }}>{layoutVersion || 'unknown'}</span>
        </div>
        {engine && <div>engine: {engine}</div>}
        {layoutMode && <div>mode: {layoutMode}</div>}
      </div>

      {warnings.length > 0 && (
        <div
          className="mt-1.5 pt-1.5 border-t border-white/15"
          style={{ color: '#fbbf24', fontSize: 9 }}
          data-testid="perf-warnings"
        >
          {warnings.map((w, i) => (
            <div key={i}>⚠ {w}</div>
          ))}
        </div>
      )}
    </div>
  );
}
